/**
 * A node's state as an ordered list of short clauses — the list itself, decided
 * once, for the three surfaces that print it at one line's budget or more: the
 * card's status line, the thread's folded strip and the path's ancestor cards.
 *
 * `statusWords.ts` already held the words and the rules that choose between
 * them; each surface still assembled its own LIST, and the lists had drifted.
 * The card said an insight's prior-art reading and "not checked yet" for an
 * unread claim; the strip and the ancestor cards said neither, so a folded
 * insight read as its kind alone and an unchecked claim on the path read the
 * same as one nobody had said anything about (D335). The strip is the card's
 * stand-in when folded, and it omitted the one fact that prompts the next act.
 *
 * Words only. The card renders some of these as presses (the refuted claim, the
 * changed record) and adds clauses only it has room for — the report count, an
 * objection that stood, the rewrite — by key, around this list rather than in a
 * copy of it.
 *
 * A leaf on the consumer side, like `statusWords.ts`: the UI imports it and
 * nothing in `core/` does.
 */

import type { ReasonNode } from './types'
import { ENDING_LABEL, endingOf } from './endings'
import type { RefutedUnder } from './taint'
import { STATUS_WORD } from './vocabulary'
import { calendarDay } from './dates'
import { outcomeWord, priorArtWord, readingWord, retractedWord, standsOnWord } from './statusWords'

export type StateClauseKey =
  | 'ending'
  | 'survey'
  | 'reading'
  | 'prior-art'
  | 'unchecked'
  | 'retracted'
  | 'outcome'
  | 'refuted'
  | 'rebase'

export interface StateClause {
  key: StateClauseKey
  /** Lower case, as the clause sits inside a line; a line that OPENS with one
   *  restores the capital itself — except the ending, which is the table's. */
  word: string
}

const low = (w: string): string => w.toLowerCase()

/**
 * In this order: the ending; the seed's survey, or the claim's reading, or an
 * insight's prior-art word, or "not checked yet"; a withdrawn work the reading
 * cites; a result that settled nothing; the refuted claim it stands on; and a
 * changed record it stands before that the refutation does not already name
 * (`secondLookChange`, which the caller has applied).
 *
 * "Not checked yet" never on an insight (D288, D289): its check is its prior-art
 * search, and the prior-art word says whether that has run. Never on the seed
 * either, whose clause is the survey's (D143), and never over a recorded result,
 * which is something outside the model having met the claim.
 */
export function stateClauses(
  n: ReasonNode,
  {
    refuted,
    evidenceChange,
  }: {
    /** The refuted claim it was reasoned from (`refutedUnder`). */
    refuted?: RefutedUnder | null
    /** The changed record to still name, or none (`secondLookChange`). */
    evidenceChange?: { rebasedAt?: number } | null
  } = {},
): StateClause[] {
  const out: StateClause[] = []
  const isRoot = n.parentId === null
  const ending = endingOf(n)
  if (ending) out.push({ key: 'ending', word: ENDING_LABEL[ending] })
  if (isRoot) {
    out.push({
      key: 'survey',
      word: n.survey
        ? `${low(STATUS_WORD.fieldSurveyed)} ${calendarDay(n.survey.surveyedAt)}`
        : low(STATUS_WORD.fieldNotSurveyed),
    })
  } else {
    const reading = readingWord(n)
    if (reading) out.push({ key: 'reading', word: reading })
    const searched = priorArtWord(n)
    if (searched) out.push({ key: 'prior-art', word: searched })
    if (!reading && !searched && !n.outcome)
      out.push({ key: 'unchecked', word: low(STATUS_WORD.notChecked) })
  }
  const retracted = retractedWord(n)
  if (retracted) out.push({ key: 'retracted', word: retracted })
  const outcome = outcomeWord(n)
  if (outcome) out.push({ key: 'outcome', word: outcome })
  if (refuted) out.push({ key: 'refuted', word: standsOnWord(refuted, false) })
  if (evidenceChange)
    out.push({
      key: 'rebase',
      word: low(evidenceChange.rebasedAt ? STATUS_WORD.priorEvidence : STATUS_WORD.needsSecondLook),
    })
  return out
}
